import { GoogleGenAI } from "@google/genai";
import { Project, ProjectCategory, ChatMessage } from "../types";

// Initialize Gemini Client
const ai = new GoogleGenAI({ apiKey: process.env.API_KEY });

/**
 * Analyzes text data (Title/Description) to infer Tags and Category.
 * This replaces the direct URL visit which often fails.
 */
export const analyzeProjectText = async (title: string, description: string): Promise<Partial<Project>> => {
  try {
    const prompt = `
      I am adding a new project to my portfolio. 
      Title: ${title}
      Description: ${description}
      
      Based on this, please infer 3 relevant Tags and the most appropriate Category.
      Categories must be one of: ['Web App', 'Frontend', 'Full-Stack', '3D/Creative', 'Utility'].
      
      Return the result as a strictly valid JSON object with the keys: "tags" (array of strings) and "category".
      Do not include markdown formatting like \`\`\`json.
    `;

    const response = await ai.models.generateContent({
      model: 'gemini-flash-lite-latest',
      contents: [{ role: 'user', parts: [{ text: prompt }] }],
      config: {
        temperature: 0.3,
        responseMimeType: "application/json"
      }
    });

    const text = response.text?.trim() || "{}";
    
    // Improved JSON extraction: find the first { and the last }
    const jsonMatch = text.match(/\{[\s\S]*\}/);
    const jsonStr = jsonMatch ? jsonMatch[0] : "{}";
    
    return JSON.parse(jsonStr);
  } catch (error) {
    console.error("Error analyzing text with AI:", error);
    // Return empty but don't force 'New Project' here to allow caller to see what happened if needed
    // However, for safety we return sensible defaults
    return {
      category: 'Web App',
      tags: ['New']
    };
  }
};

/**
 * Deprecated: Kept for compatibility but redirects to text analysis if used.
 */
export const generateProjectFromUrl = async (url: string): Promise<Partial<Project>> => {
    // This is handled in ProjectFormModal now via Microlink + analyzeProjectText
    return {
      title: 'New Project',
      description: 'Please use Magic Fill in the form.',
      category: 'Web App',
      tags: ['New']
    };
};

/**
 * Automatically categorizes a project based on its details.
 */
export const categorizeProject = async (title: string, description: string, url: string): Promise<ProjectCategory> => {
  try {
    const prompt = `
      Analyze the following project details and assign it exactly one of these categories: 
      ['Web App', 'Frontend', 'Full-Stack', '3D/Creative', 'Utility'].
      
      Project: ${title}
      Description: ${description}
      URL: ${url}
      
      Return ONLY the category name.
    `;

    const response = await ai.models.generateContent({
      model: 'gemini-flash-lite-latest',
      contents: [{ role: 'user', parts: [{ text: prompt }] }],
      config: {
        temperature: 0.1
      }
    });

    const text = response.text?.trim();
    const validCategories: ProjectCategory[] = ['Web App', 'Frontend', 'Full-Stack', '3D/Creative', 'Utility'];
    
    if (text && validCategories.includes(text as ProjectCategory)) {
      return text as ProjectCategory;
    }
    return 'Web App';
  } catch (error) {
    console.error("Error categorizing project:", error);
    return 'Web App';
  }
};

/**
 * Generates a chat response for the portfolio AI assistant.
 */
export const generateChatResponse = async (
  history: ChatMessage[],
  currentMessage: string,
  projects: Project[]
): Promise<string> => {
  try {
    const projectContext = projects.map(p => 
      `- ${p.title} (${p.category}): ${p.description}. URL: ${p.url}. Tags: ${p.tags.join(', ')}`
    ).join('\n');

    const systemInstruction = `You are an AI assistant for a developer portfolio website. 
    Your role is to answer questions about the projects and the developer based on the context provided.
    Be friendly, professional, and concise.
    
    Here is the list of projects in the portfolio:
    ${projectContext}
    
    If asked about skills, infer them from the project tags and descriptions.
    If asked about contact info, say you don't have access to private contact details but they can check the footer social links.
    `;

    const contents = history
      .filter(msg => msg.id !== 'welcome')
      .map(msg => ({
        role: msg.role,
        parts: [{ text: msg.text }]
      }));

    contents.push({
      role: 'user',
      parts: [{ text: currentMessage }]
    });

    const response = await ai.models.generateContent({
      model: 'gemini-3-flash-preview',
      contents: contents,
      config: {
        systemInstruction: systemInstruction,
      }
    });

    return response.text || "I'm sorry, I couldn't generate a response.";
  } catch (error) {
    console.error("Chat Error:", error);
    return "I'm having trouble connecting to the AI right now. Please try again later.";
  }
};